import type { InstructorPrivateLessonDTO } from "@/modules/master/domain/contracts";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { formatSlotDurationLaoLabel } from "@/shared/lib/private-lesson-slot-duration";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { CheckCircleIcon, XCircleIcon } from "lucide-react";

type InstructorPrivateLessonsTableProps = {
  data: InstructorPrivateLessonDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, _limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
};

export function InstructorPrivateLessonsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: InstructorPrivateLessonsTableProps) {
  const columns: TanstackReactTable.ColumnDef<InstructorPrivateLessonDTO>[] = [
    createSortableColumn("date", "ວັນທີ", {
      cell: ({ row }) => (
        <div>
          <div className="font-semibold">{formatDate(row.original.date)}</div>
          <div className="text-muted-foreground text-xs">
            {formatTime(row.original.startTime)} -{" "}
            {formatTime(row.original.endTime)}
          </div>
        </div>
      ),
    }),
    {
      header: "ນັກຮຽນ",
      accessorKey: "studentName",
      cell: ({ row }) => (
        <div>
          <div className="font-semibold">{row.original.studentName}</div>
          <div className="text-muted-foreground text-xs">
            {row.original.studentMemberId}
          </div>
        </div>
      ),
    },
    {
      header: "ຫ້ອງ",
      accessorKey: "roomName",
      cell: ({ row }) => row.original.roomName || "-",
    },
    {
      header: "ລາຍລະອຽດ",
      accessorKey: "durationMinutes",
      cell: ({ row }) => {
        const minutes = row.original.durationMinutes;
        return formatSlotDurationLaoLabel(minutes) ?? "-";
      },
    },
    createSortableColumn("status", "ສະຖານະ", {
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "completed"
            ? "default"
            : status === "cancelled"
              ? "destructive"
              : "outline";
        const label =
          status === "completed"
            ? "ສຳເລັດ"
            : status === "cancelled"
              ? "ຍົກເລີກ"
              : "ຈັດຕາຕະລາງ";
        return <Badge variant={variant}>{label}</Badge>;
      },
    }),
    {
      header: "Check-in",
      accessorKey: "hasCheckIn",
      cell: ({ row }) => {
        const hasCheckIn = row.original.hasCheckIn;
        return hasCheckIn ? (
          <div className="flex items-center justify-center">
            <CheckCircleIcon className="size-4 text-green-600" />
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <XCircleIcon className="size-4 text-muted-foreground" />
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
